import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { apiError, getBuyer } from "@/lib/api";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import AthmButton from "@/components/AthmButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CircleNotch, Trash, ShoppingCart, ArrowLeft, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function StoreCart() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tokenKey = `hephy_cart_${slug}`;
  const [token] = useState(() => localStorage.getItem(tokenKey));
  const [cart, setCart] = useState(null);
  const [store, setStore] = useState(null);
  const [method, setMethod] = useState(null);
  const buyer = getBuyer();
  const [email, setEmail] = useState(buyer?.email || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [buyerName, setBuyerName] = useState(buyer?.name || "");
  const [phone, setPhone] = useState("");
  const [placed, setPlaced] = useState(null);

  const load = () => {
    if (!token) { setCart({ items: [], subtotal: 0 }); return; }
    api.get(`/public/cart/${token}`, { withCredentials: false })
      .then((r) => setCart(r.data))
      .catch(() => setCart({ items: [], subtotal: 0 }));
  };
  useEffect(load, [token]);
  useEffect(() => {
    api.get(`/public/store/${slug}`, { withCredentials: false })
      .then((r) => { setStore(r.data.store); setMethod(r.data.store?.checkout_enabled ? "stripe" : (r.data.store?.athm_enabled ? "athm" : null)); })
      .catch(() => {});
  }, [slug]);

  const rep = (() => { try { return JSON.parse(localStorage.getItem(`hephy_rep_${slug}`) || "null"); } catch { return null; } })();
  const promo = (() => { try { return localStorage.getItem(`hephy_promo_${slug}`) || null; } catch { return null; } })();
  const emailOk = email.trim() && email.includes("@");

  const remove = async (itemId) => {
    try {
      const { data } = await api.post(`/public/cart/${token}/remove`, { item_id: itemId }, { withCredentials: false });
      setCart(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  const checkout = async () => {
    if (!emailOk) { toast.error(t("cart.invalidEmail")); return; }
    setLoading(true); setErr(null);
    try {
      const { data } = await api.post(`/public/cart/${token}/checkout`, { email: email.trim(), buyer_user_id: buyer?.id || null, rep_name: rep?.name || null, rep_phone: rep?.phone || null, promo_code: promo }, { withCredentials: false });
      window.location.assign(data.checkout_url);
    } catch (e) { setErr(apiError(e)); toast.error(apiError(e)); setLoading(false); }
  };

  const manual = store && !store.checkout_enabled && !store.athm_enabled;
  const placeOrder = async () => {
    if (!emailOk) { toast.error(t("cart.invalidEmail")); return; }
    if (!buyerName.trim()) { toast.error("Ingresa tu nombre."); return; }
    setLoading(true); setErr(null);
    try {
      const { data } = await api.post(`/public/cart/${token}/place-order`, { email: email.trim(), name: buyerName.trim(), phone: phone.trim(), buyer_user_id: buyer?.id || null, rep_name: rep?.name || null, rep_phone: rep?.phone || null, promo_code: promo }, { withCredentials: false });
      localStorage.removeItem(tokenKey);
      setPlaced(data);
    } catch (e) { setErr(apiError(e)); toast.error(apiError(e)); } finally { setLoading(false); }
  };

  if (placed) return (
    <div className="min-h-screen bg-[#0B0B0F] text-neutral-100 flex items-center justify-center px-6" data-testid="order-placed">
      <div className="max-w-md text-center border border-white/10 rounded-2xl p-8 bg-white/[0.03]">
        <h1 className="text-2xl font-bold mb-2">¡Pedido recibido!</h1>
        <p className="text-sm text-neutral-400">Total: <span className="text-gold font-semibold">${Number(placed.amount).toLocaleString()}</span>. La tienda te contactará a <b>{email}</b> para coordinar el pago y la entrega.</p>
        <div className="flex flex-col gap-2 mt-6">
          {placed.status_token && <Link to={`/pedido/${placed.status_token}`} className="text-sm text-gold hover:underline" data-testid="order-placed-track">Ver estado del pedido</Link>}
          <Link to={`/tienda/${slug}`} className="text-sm text-neutral-400 hover:text-white" data-testid="order-placed-back">Volver a la tienda</Link>
        </div>
      </div>
    </div>
  );

  if (!cart) return <div className="min-h-screen bg-[#0B0B0F] flex items-center justify-center"><CircleNotch size={30} className="animate-spin text-gold" /></div>;

  const empty = !cart.items?.length;

  return (
    <div className="min-h-screen bg-[#0B0B0F] text-neutral-100" data-testid="store-cart-page">
      <LanguageSwitcher className="fixed bottom-4 right-4 z-30" />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to={`/tienda/${slug}`} className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-gold mb-6"><ArrowLeft size={15} /> {t("common.backToStore")}</Link>
        <h1 className="text-3xl font-bold tracking-tight mb-8 flex items-center gap-3"><ShoppingCart size={26} weight="duotone" className="text-gold" /> {t("cart.title")}</h1>

        {empty ? (
          <div className="border border-dashed border-white/15 rounded-2xl p-16 text-center text-neutral-500" data-testid="cart-empty">
            {t("cart.empty")} <Link to={`/tienda/${slug}`} className="text-gold underline">{t("cart.explore")}</Link>
          </div>
        ) : (
          <>
            <div className="space-y-3" data-testid="cart-items">
              {cart.items.map((it) => (
                <div key={it.id} className="flex items-start gap-4 border border-white/10 rounded-xl p-4 bg-white/[0.03]" data-testid={`cart-item-${it.id}`}>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold">{it.name}</h3>
                    {it.customization_text && <p className="text-xs text-neutral-400 mt-1 line-clamp-2">{it.customization_text}</p>}
                    <p className="text-xs text-neutral-500 mt-1">{t("common.quantity")}: {it.quantity}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-serif text-lg">${(it.unit_amount * it.quantity).toLocaleString()}</div>
                    <button onClick={() => remove(it.id)} className="text-neutral-500 hover:text-red-400 mt-2" data-testid={`cart-remove-${it.id}`}><Trash size={16} /></button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-white/10 mt-6 pt-6 flex items-center justify-between">
              <span className="text-neutral-400">{t("common.subtotal")}</span>
              <span className="text-2xl font-serif" data-testid="cart-subtotal">${Number(cart.subtotal).toLocaleString()}</span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">{t("cart.taxesNote")}</p>

            <div className="mt-8 space-y-3">
              <label className="text-sm text-neutral-300">{t("cart.emailLabel")}</label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("cart.emailPlaceholder")} className="bg-white/5 border-white/15" data-testid="cart-email-input" />
              {err && <p className="text-xs text-red-300" data-testid="cart-error">{err}</p>}

              {store && store.checkout_enabled && store.athm_enabled && (
                <div className="flex gap-2 pt-1" data-testid="payment-method-select">
                  <button onClick={() => setMethod("stripe")} data-testid="method-stripe" className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium transition-all ${method === "stripe" ? "border-gold bg-gold/10 text-white" : "border-white/15 text-neutral-400"}`}>Tarjeta</button>
                  <button onClick={() => setMethod("athm")} data-testid="method-athm" className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium transition-all ${method === "athm" ? "border-gold bg-gold/10 text-white" : "border-white/15 text-neutral-400"}`}>ATH Móvil</button>
                </div>
              )}

              {manual ? (
                <div className="space-y-3" data-testid="manual-order-section">
                  <Input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} placeholder="Tu nombre *" className="bg-white/5 border-white/15" data-testid="cart-name-input" />
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Teléfono (opcional)" className="bg-white/5 border-white/15" data-testid="cart-phone-input" />
                  <Button onClick={placeOrder} disabled={loading} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold h-12" data-testid="cart-place-order-btn">
                    {loading ? <CircleNotch className="animate-spin" /> : "Enviar pedido"}
                  </Button>
                  <p className="text-[11px] text-neutral-500 text-center">Tu pedido queda guardado en la tienda. Te contactarán para coordinar el pago.</p>
                </div>
              ) : method === "athm" ? (
                emailOk
                  ? <div className="pt-1" data-testid="athm-section"><AthmButton slug={slug} cartToken={token} email={email.trim()} rep={rep} promo={promo} onPaid={() => navigate(`/tienda/${slug}?pago=ok`)} /></div>
                  : <p className="text-xs text-neutral-500 pt-1">Ingresa tu correo para pagar con ATH Móvil.</p>
              ) : (
                <Button onClick={checkout} disabled={loading || !store?.checkout_enabled} className="w-full rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold h-12" data-testid="cart-checkout-btn">
                  {loading ? <CircleNotch className="animate-spin" /> : <><Lock size={16} className="mr-2" /> {t("cart.pay")}</>}
                </Button>
              )}
              {!manual && <p className="text-[11px] text-neutral-600 text-center">{t("cart.payNote")}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
